import type { OfferItem, OfferStatus } from "./types";

/**
 * Extracted from `NftFinanceSection` (Phase 11 finance hotfix) so the
 * Accept/Cancel eligibility rules are unit-testable without a React render
 * harness — this repo's test runner has no DOM.
 *
 * WHY THESE ARE "NOT TERMINAL" CHECKS AND NOT ALLOW-LISTS. The previous rules
 * were `status === "success"` for Accept and `pending | submitted` for Cancel.
 * Neither state is one a freshly created native offer actually reaches: the
 * create endpoint returns the offer in its own opening status, so the owner's
 * Accept button was gated on a status the offer never had — that is the
 * "an offer can be created but the owner can never accept it" bug, and it
 * survived the Phase 11 endpoint fix because it lives here, not in `api.ts`.
 *
 * The backend is authoritative on whether an offer is acceptable/cancellable
 * (it answers 400 with `errors[0]` when it isn't, and that reason is shown).
 * The client only hides the action for states that are unambiguously finished,
 * so it never invents a rule the API does not have.
 */
const TERMINAL_STATUSES: readonly OfferStatus[] = [
  "accepted",
  "cancelled",
  "expired",
  // Set by the backend on the other active offers once one is accepted.
  "obsolete",
  // A handshake/job/webhook failure. Finished, and nothing to act on.
  "fail",
];

/**
 * Already moving, on someone's instruction, and not finished.
 *
 * NOT terminal — this offer may well end up accepted or cancelled — but the
 * ACTION is already under way, and offering it again is offering to do it
 * twice. `pending_cancel` next to a live Cancel button is a second cancel;
 * `pending_accept` next to a live Accept is a second sale attempt.
 *
 * The plain `pending*` states of PLACING (`pending`, `pending_wrap`,
 * `pending_approve`, `pending_signature`) are deliberately absent: they say
 * the offer itself is still settling, which does not stop its owner
 * cancelling it or the seller accepting it once it is live. The backend is
 * the authority on that and answers 400 with a reason when it disagrees.
 */
const IN_PROGRESS_STATUSES: readonly OfferStatus[] = [
  "pending_cancel",
  "pending_accept",
  "pending_accept_approve",
];

function isTerminal(status: OfferStatus): boolean {
  return TERMINAL_STATUSES.includes(status);
}

function isInProgress(status: OfferStatus): boolean {
  return IN_PROGRESS_STATUSES.includes(status);
}

/** Neither finished nor already being acted on. */
function isActionable(status: OfferStatus): boolean {
  return !isTerminal(status) && !isInProgress(status);
}

/** Accept: the NFT owner only, while the offer is not already finished. */
export function canAcceptOffer(offer: Pick<OfferItem, "status">, isOwner: boolean): boolean {
  return isOwner && isActionable(offer.status);
}

/**
 * The statuses in which an offer can be cancelled — SPECIFIED BY THE BACKEND,
 * not derived here.
 *
 * The client used to infer this ("not finished, and not already being acted
 * on"), and that inference put `pending_cancel` out of reach: a cancellation
 * already under way looked like an action in progress. But a cancel job can
 * FAIL, which leaves the offer live again, so its author has to be able to ask
 * a second time.
 *
 * `pending` IS CANCELLABLE — but only for a custodial wallet; the backend
 * refuses it for an external one. That distinction is NOT made here, on
 * purpose: this client cannot reliably tell which kind of wallet is behind an
 * offer, and the backend answers 400 with its own reason when it disagrees —
 * which `submitOfferDecision` already surfaces verbatim. Hiding the control on
 * a guess is the worse failure: an author with no way to cancel and no
 * explanation, instead of a refusal that says why.
 */
export const CANCELLABLE_OFFER_STATUSES: readonly OfferStatus[] = [
  "pending",
  "submitted",
  "success",
  "pending_cancel",
];

/**
 * Cancel: the offer's own author only, in one of the statuses above. Owning
 * the NFT grants no cancel right — only authorship does.
 *
 * TWO WAYS TO BE THE AUTHOR, because the list endpoint changed underneath
 * this. The non-V2 offers feed named the author with `user_id`; V2 — which is
 * what the app now calls, and the only one that shows an author their own
 * `pending*` offers — sends no `user_id` at all, only the `offerer` address.
 *
 * Either match is sufficient and neither is required to be present. Addresses
 * are compared case-insensitively: the same address is spelled checksummed in
 * some payloads and lowercased in others, and a string compare would read
 * those as two different people.
 */
export function canCancelOffer(
  offer: Pick<OfferItem, "status" | "user_id" | "offerer_address">,
  viewerId: number | null,
  /** The viewer's own wallet addresses. Empty when they are not known yet. */
  viewerAddresses: readonly (string | null | undefined)[] = [],
): boolean {
  if (!CANCELLABLE_OFFER_STATUSES.includes(offer.status)) return false;

  const offerUserId = offer.user_id ? Number(offer.user_id) : null;
  const matchesId = viewerId != null && offerUserId === viewerId;

  const offerer = offer.offerer_address?.trim().toLowerCase();
  const matchesAddress =
    !!offerer &&
    viewerAddresses.some((address) => address?.trim().toLowerCase() === offerer);

  return matchesId || matchesAddress;
}
